import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Download, Trash2, ShieldCheck, ShieldAlert, RefreshCw, UploadCloud } from "lucide-react";

function EnvInventoryCard() {
  const [rows, setRows] = useState(null);

  const load = () => api.get("/platform/diagnostics/env-inventory").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="env-inventory-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-[#09090B]">Inventario de variables de entorno</h3>
        <Button variant="ghost" size="icon" onClick={load} data-testid="env-inventory-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /> : (
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Variable</TableHead>
                <TableHead>Obligatoria</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Valor efectivo</TableHead>
                <TableHead>En la migración</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.name} data-testid={`env-row-${r.name}`}>
                  <TableCell className="font-mono text-xs">{r.name}{r.is_secret && <span className="text-[#A1A1AA]"> (secreto)</span>}</TableCell>
                  <TableCell>{r.required ? "Sí" : "No"}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 text-[10px] rounded-sm ${r.configured ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>
                      {r.configured ? "configurada" : "ausente"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#71717A]">{r.effective_value ?? (r.default != null ? `default: ${r.default}` : "—")}</TableCell>
                  <TableCell className="text-xs">
                    {r.migration_rule === "same" && <span className="text-[#0055FF]">tiene que ser igual</span>}
                    {r.migration_rule === "different" && <span className="text-[#F59E0B]">tiene que ser distinta</span>}
                    {r.note && <div className="text-[#A1A1AA] mt-0.5">{r.note}</div>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function CredentialsCheckCard() {
  const [data, setData] = useState(null);

  const load = () => api.get("/platform/diagnostics/credentials-check").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="credentials-check-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-[#09090B]">Chequeo de descifrado de credenciales</h3>
        <Button variant="ghost" size="icon" onClick={load} data-testid="credentials-check-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /> : (
        <div>
          <div className="flex gap-4 mb-3 text-sm">
            <span className="flex items-center gap-1 text-[#10B981]" data-testid="credentials-total-ok"><ShieldCheck className="h-4 w-4" /> {data.total_ok} descifran bien</span>
            <span className="flex items-center gap-1 text-[#EF4444]" data-testid="credentials-total-failed"><ShieldAlert className="h-4 w-4" /> {data.total_failed} fallan</span>
          </div>
          {data.by_tenant.map((t) => (
            <div key={t.tenant_id} className="text-xs border-t border-[#E4E4E7] py-2">
              <div className="font-medium text-[#27272A]">Tenant {t.tenant_id} — {t.ok} ok / {t.failed} fallan</div>
              {t.details.map((d, i) => (
                <div key={i} className="text-[#71717A] flex items-center gap-2 mt-0.5">
                  {d.ok ? <ShieldCheck className="h-3 w-3 text-[#10B981]" /> : <ShieldAlert className="h-3 w-3 text-[#EF4444]" />}
                  {d.collection} · {d.platform} · {d.label}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ExportCard() {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setInterval(() => {
      api.get(`/platform/migration/export/${job.id}`).then((r) => setJob(r.data));
    }, 3000);
    return () => clearInterval(t);
  }, [job]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/platform/migration/export");
      const status = await api.get(`/platform/migration/export/${r.data.job_id}`);
      setJob(status.data);
      toast.success("Export iniciado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el export");
    } finally {
      setStarting(false);
    }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await api.delete(`/platform/migration/export/${job.id}`);
      toast.success("Artefacto borrado (contenido sobrescrito en el storage)");
      setJob({ ...job, status: "deleted" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    } finally {
      setDeleting(false);
    }
  };

  const dl = (path, filename) => {
    const url = `${api.defaults.baseURL}/platform/migration/export/${job.id}/download/${path}`;
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="export-card">
      <h3 className="font-heading font-bold text-[#09090B] mb-1">Exportar deploy completo</h3>
      <p className="text-xs text-[#71717A] mb-3">Base de datos (Extended JSON canónico) + archivos, en ZIP. Vive {6}hs en el storage o hasta que lo borres.</p>
      <Button onClick={start} disabled={starting || job?.status === "running"} data-testid="start-export-button" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
        {starting || job?.status === "running" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
        {job?.status === "running" ? "Exportando..." : "Iniciar exportación"}
      </Button>

      {job && (
        <div className="mt-4 text-sm space-y-2" data-testid="export-job-status">
          <div>Estado: <Badge className="border-0 rounded-sm bg-[#0055FF]/10 text-[#0055FF]">{job.status}</Badge> {job.stage_detail && `(${job.stage}: ${job.stage_detail})`}</div>
          {job.status === "error" && <div className="text-[#EF4444]">{job.error}</div>}
          {job.status === "done" && job.manifest_summary && (
            <>
              <div className="text-xs text-[#71717A]">
                {job.manifest_summary.total_documents} documentos en {job.manifest_summary.collections} colecciones ·
                {" "}{job.manifest_summary.object_keys_count} archivos ({Math.round(job.manifest_summary.object_keys_total_bytes / 1024)} KB)
                {job.manifest_summary.objectid_warnings?.length > 0 && (
                  <span className="text-[#F59E0B]"> · {job.manifest_summary.objectid_warnings.length} ObjectId fuera de _id</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-md" onClick={() => dl("manifest", "manifest.json")} data-testid="download-manifest-button">
                  <Download className="h-3.5 w-3.5 mr-1" /> manifest.json
                </Button>
                <Button size="sm" variant="outline" className="rounded-md" onClick={() => dl("db", "db.zip")} data-testid="download-db-button">
                  <Download className="h-3.5 w-3.5 mr-1" /> db.zip
                </Button>
                {job.file_parts?.map((p, i) => (
                  <Button key={i} size="sm" variant="outline" className="rounded-md" onClick={() => dl(`files/${i + 1}`, p.key.split("/").pop())} data-testid={`download-filepart-${i + 1}`}>
                    <Download className="h-3.5 w-3.5 mr-1" /> parte {i + 1}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" className="text-[#EF4444] rounded-md" disabled={deleting} onClick={del} data-testid="delete-export-button">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Borrar ya
                </Button>
              </div>
            </>
          )}
          {job.status === "deleted" && <div className="text-xs text-[#71717A]">Contenido sobrescrito en el storage.</div>}
        </div>
      )}
    </Card>
  );
}

function ImportCard() {
  const [manifestFile, setManifestFile] = useState(null);
  const [dbZipFile, setDbZipFile] = useState(null);
  const [fileParts, setFileParts] = useState([]);
  const [confirmDbName, setConfirmDbName] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!manifestFile || !dbZipFile || !confirmDbName.trim()) {
      toast.error("Faltan el manifest.json, el db.zip o el nombre de confirmación");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("manifest", manifestFile);
      form.append("db_zip", dbZipFile);
      form.append("confirm_db_name", confirmDbName.trim());
      form.append("force_overwrite_existing_data", force ? "true" : "false");
      for (const f of fileParts) form.append("file_parts", f);
      const r = await api.post("/platform/migration/import", form);
      setResult(r.data);
      toast.success(r.data.overall_ok ? "Import completo y verificado" : "Import terminó con diferencias -- revisá la tabla");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="import-card">
      <h3 className="font-heading font-bold text-[#09090B] mb-1">Importar deploy completo</h3>
      <p className="text-xs text-[#71717A] mb-3">Reemplaza TODA la base del destino. Subí los archivos que bajaste del origen.</p>
      <div className="space-y-2">
        <div>
          <Label className="text-xs text-[#71717A]">manifest.json</Label>
          <Input type="file" accept=".json" onChange={(e) => setManifestFile(e.target.files[0])} data-testid="import-manifest-input" />
        </div>
        <div>
          <Label className="text-xs text-[#71717A]">db.zip</Label>
          <Input type="file" accept=".zip" onChange={(e) => setDbZipFile(e.target.files[0])} data-testid="import-dbzip-input" />
        </div>
        <div>
          <Label className="text-xs text-[#71717A]">Partes de archivos (files-part-XX.zip, podés elegir varias)</Label>
          <Input type="file" accept=".zip" multiple onChange={(e) => setFileParts(Array.from(e.target.files))} data-testid="import-fileparts-input" />
        </div>
        <div>
          <Label className="text-xs text-[#71717A]">Escribí el nombre de la base de ESTE destino para confirmar</Label>
          <Input value={confirmDbName} onChange={(e) => setConfirmDbName(e.target.value)} data-testid="import-confirm-dbname-input" />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox checked={force} onCheckedChange={setForce} data-testid="import-force-checkbox" />
          Pisar datos existentes más allá de la semilla (solo si estás seguro)
        </label>
        <Button onClick={run} disabled={busy} className="bg-[#EF4444] hover:bg-[#DC2626] rounded-md" data-testid="run-import-button">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1" />} Importar
        </Button>
      </div>

      {result && (
        <div className="mt-4 space-y-2" data-testid="import-result">
          <div className={`text-sm font-medium ${result.overall_ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
            {result.overall_ok ? "Todo coincide" : "Hay diferencias -- revisá abajo"}
          </div>
          <div className="text-xs">Seeds crearon datos nuevos: {result.seeds_created_extra_data ? <span className="text-[#EF4444]">SÍ (mal)</span> : "no"}</div>
          <div className="text-xs">Archivos: {result.objects.uploaded}/{result.objects.expected} subidos, {result.objects.missing_after_reconciliation.length} faltantes</div>
          <div className="max-h-72 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Colección</TableHead><TableHead>Origen</TableHead><TableHead>Destino</TableHead><TableHead>Coincide</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(result.collections).map(([name, c]) => (
                  <TableRow key={name} className={c.match ? "" : "bg-[#EF4444]/5"} data-testid={`import-row-${name}`}>
                    <TableCell className="text-xs">{name}</TableCell>
                    <TableCell className="text-xs">{c.source_count}</TableCell>
                    <TableCell className="text-xs">{c.dest_count}</TableCell>
                    <TableCell className="text-xs">{c.match ? "✓" : <span className="text-[#EF4444] font-medium">✗</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
}

function Label({ className, children }) {
  return <div className={`mb-1 ${className}`}>{children}</div>;
}

export default function MigrationPage() {
  return (
    <div className="space-y-6" data-testid="migration-page">
      <div>
        <h2 className="font-heading text-xl font-bold text-[#09090B]">Migración completa de deploy</h2>
        <p className="text-sm text-[#71717A]">Solo operador de plataforma. Ver runbook en MIGRATION.md para el paso a paso del corte.</p>
      </div>
      <EnvInventoryCard />
      <CredentialsCheckCard />
      <ExportCard />
      <ImportCard />
    </div>
  );
}
